import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import { TFunction } from 'i18next';
import { IDocument } from 'interfaces';
import { RootState } from 'store';
import { setAnalysisProcessStatus, setSessionStatus, setTranscription } from 'store/slices/aiFunctionsDataSlice';

type propsCheckRequest = {
  document: IDocument;
  t: TFunction;
}

export const checkTranscription = createAsyncThunk('transcription/check', async (props: propsCheckRequest, thunkApi) => {
  const rootState = thunkApi.getState() as RootState;
  const { messageApi } = rootState.windowState;
  
  try {
    const transcription = await api.checkTranscription({document_id: props.document.id});
    return transcription.data;
  } catch (err: any) {    
    if (err?.response?.data?.message === "Transcription entity doesn't exist") { 
      return {status: 'not_found'}
    } else {
      messageApi?.error(props.t('Documents.details.transcription.errorGettingTranscription'), 8);
    }
  }
});

export const updateTranscription = createAsyncThunk('transcription/update', async (props: {document_id: string, t: TFunction, message: string, status: string}, thunkApi) => {
  const rootState = thunkApi.getState() as RootState;
  const { dispatch } = thunkApi;
  const { selectedDocument } = rootState.documents;
  const { messageApi } = rootState.windowState;

  if (selectedDocument?.id === props.document_id) {
    dispatch(setSessionStatus(props.status.toLocaleLowerCase()));

    if (props.status === 'READY') {
      try {
        const transcription = await api.checkTranscription({document_id: props.document_id});
        dispatch(setTranscription(transcription.data));
      } catch (err: any) {
        console.log(err, 'transcription/update error');
      } 
    } else if (props.status === 'FAILED') {
      dispatch(setTranscription({ status: 'not_found' }));
      messageApi?.error(props.t('Documents.details.transcription.errorTranscribation'), 8);
    }
  }

});

export const checkAnalysis = createAsyncThunk('analysis/check', async (props: propsCheckRequest, thunkApi) => {
  const rootState = thunkApi.getState() as RootState;
  const { dispatch } = thunkApi;
  const { messageApi } = rootState.windowState;
  try {
    const analysis = await api.checkAnalysis({document_id: props.document.id});
    if (analysis.data?.status === 'in_progress') {
      dispatch(setAnalysisProcessStatus('in_progress'));
      return {status: 'in_progress'}
    } else {
      dispatch(setAnalysisProcessStatus('downloading_report'));
      return {status: 'in_progress'}
    }    
  } catch (err: any) {    
    if (err?.response?.data?.detail === "Report doesn't exist") { 
      return {status: 'not_found'}
    } else {
      messageApi?.error(props.t('Documents.details.analysisTab.errorGettingAnalytics'), 8);
      return {status: 'unavailable'}
    }
  }
});

export const getAnalysisOfDocument = createAsyncThunk('analysis/get_report_analysis', async (props: propsCheckRequest, thunkApi) => {
  const rootState = thunkApi.getState() as RootState;
  const { messageApi } = rootState.windowState;
  try {
    const transcription = await api.getAnalysisReport({document_id: props.document.id});
    return { file: transcription.data, status: 'ready' }
  } catch (err: any) {    
    messageApi?.error(props.t('Documents.details.analysisTab.errorGettingAnalytics'), 8);
    return {status: 'not_found'};
  }
});

export const updateAnalysisStatus = createAsyncThunk('analysis/update', async (props: {document_id: string, t: TFunction, message: string, status: string}, thunkApi) => {
  const rootState = thunkApi.getState() as RootState;
  const { dispatch } = thunkApi;
  const { selectedDocument } = rootState.documents;
  const { messageApi } = rootState.windowState;

  if (selectedDocument?.id === props.document_id) {
    dispatch(setAnalysisProcessStatus(props.status.toLocaleLowerCase()));

    if (props.status === 'READY') {
      dispatch(setAnalysisProcessStatus('downloading_report'));
    } else if (props.status === 'FAILED') {
      dispatch(setTranscription({ status: 'not_found' }));
      messageApi?.error(props.t('Documents.details.analysisTab.parsingError'), 8);
    }
  }
});
